import { toStartOfDay } from '@/utils/deliverySchedule'
import { toLocalDateString } from '@/utils/deliveryDays'

/**
 * Planning the CRM writes behind "skip this delivery" and "cancel skip".
 *
 * CRM holidays are the single source of truth — nothing about a skip is stored
 * locally — so skipping has to express itself as create/extend/shrink/split
 * operations on existing holiday ranges. Keeping that as a pure planner means
 * the fiddly cases (merging across a gap, cancelling out of the middle of a
 * range) can be reasoned about and tested without touching the network.
 *
 * A holiday runs [start, end): `end` is the RESTART date and is NOT paused.
 */

export interface HolidayRange {
  id: string
  /** Inclusive first paused day. */
  start: Date
  /** Exclusive restart day. */
  end: Date
}

export type HolidayOp =
  | { type: 'create', start: string, end: string }
  | { type: 'update', id: string, start?: string, end?: string }
  | { type: 'delete', id: string }

const day = (d: Date): number => toStartOfDay(d).getTime()

const addDays = (d: Date, n: number): Date => {
  const next = toStartOfDay(d)
  next.setDate(next.getDate() + n)
  return next
}

/** Is any delivery scheduled in [from, to)? */
const hasDeliveryBetween = (deliveries: Date[], from: Date, to: Date): boolean =>
  deliveries.some(d => day(d) >= day(from) && day(d) < day(to))

/** The holiday covering a date, if any. */
export const holidayCovering = (date: Date, holidays: HolidayRange[]): HolidayRange | undefined =>
  holidays.find(h => day(date) >= day(h.start) && day(date) < day(h.end))

/**
 * Plan the writes to skip `date`.
 *
 * Merges into a neighbouring holiday where it can, but only when doing so
 * wouldn't swallow a delivery the customer still wants. Two skips either side
 * of a gap with no deliveries in it (a Monday and a Thursday, say) become one
 * range because that changes nothing operationally; a gap containing a live
 * delivery blocks the merge, because extending over it would silently cancel
 * a delivery nobody asked to cancel.
 */
export const planSkip = (
  date: Date,
  holidays: HolidayRange[],
  deliveries: Date[]
): HolidayOp[] => {
  const target = toStartOfDay(date)
  const dayAfter = addDays(target, 1)

  if (holidayCovering(target, holidays)) return [] // already skipped

  // A holiday that ends on or before this date, with nothing live in between.
  const before = holidays
    .filter(h => day(h.end) <= day(target))
    .filter(h => !hasDeliveryBetween(deliveries, h.end, target))
    .sort((a, b) => day(b.end) - day(a.end))[0]

  // A holiday that starts after this date, with nothing live in between.
  const after = holidays
    .filter(h => day(h.start) >= day(dayAfter))
    .filter(h => !hasDeliveryBetween(deliveries, dayAfter, h.start))
    .sort((a, b) => day(a.start) - day(b.start))[0]

  if (before && after) {
    // Bridge the two into one range and drop the now-redundant second.
    return [
      { type: 'update', id: before.id, end: toLocalDateString(after.end) },
      { type: 'delete', id: after.id },
    ]
  }

  if (before) return [{ type: 'update', id: before.id, end: toLocalDateString(dayAfter) }]
  if (after) return [{ type: 'update', id: after.id, start: toLocalDateString(target) }]

  return [{ type: 'create', start: toLocalDateString(target), end: toLocalDateString(dayAfter) }]
}

/**
 * Plan the writes to un-skip `date`.
 *
 * Shrinks the covering range from whichever end the date sits on, and splits
 * it in two when the date is in the middle — otherwise cancelling one skip in
 * a merged range would un-skip the whole range.
 */
export const planCancelSkip = (date: Date, holidays: HolidayRange[]): HolidayOp[] => {
  const target = toStartOfDay(date)
  const holiday = holidayCovering(target, holidays)
  if (!holiday) return []

  const dayAfter = addDays(target, 1)
  const atStart = day(holiday.start) === day(target)
  const atEnd = day(holiday.end) === day(dayAfter)

  if (atStart && atEnd) return [{ type: 'delete', id: holiday.id }]
  if (atStart) return [{ type: 'update', id: holiday.id, start: toLocalDateString(dayAfter) }]
  if (atEnd) return [{ type: 'update', id: holiday.id, end: toLocalDateString(target) }]

  // In the middle: keep the front half, open a new holiday for the back half.
  return [
    { type: 'update', id: holiday.id, end: toLocalDateString(target) },
    { type: 'create', start: toLocalDateString(dayAfter), end: toLocalDateString(holiday.end) },
  ]
}
